import React, { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { LayoutChangeEvent, Pressable, TextStyle, View, ViewStyle } from 'react-native'
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

import { LinearGradient } from 'expo-linear-gradient'
import { isBoolean } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  ACCORDION_CLOSE_ANIMATION_DURATION_IN_MS,
  ACCORDION_OPEN_ANIMATION_DURATION_IN_MS,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Subhead, SubheadProps, Size as SubheadSize } from '../../atoms/subhead/Subhead'
import { IS_WEB } from '../../constants'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { BodyTextSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

export type AccordionHandle = {
  open: () => void
  close: () => void
}

export type AccordionProps = {
  title: ReactNode
  content: ReactNode
  subtitle?: ReactNode
  iconComponent?: ReactNode
  testIDPrefix?: string
  titleColor?: string
  subtitleColor?: string
  subtitleSize?: BodyTextSize
  backgroundColor?: string
  style?: ViewStyle
  titleStyle?: TextStyle
  titleProps?: Partial<SubheadProps>
  buttonStyle?: ViewStyle | Array<ViewStyle>
  contentInnerContainerStyle?: ViewStyle
  handleIsExpandedChanged?: (isOpen: boolean) => void
  hideExpansionIcon?: boolean
  onAccordionPress?: () => void
  onAccordianStateChanged?: (isOpen?: boolean) => void
  isExpanded?: boolean
  initiallyOpen?: boolean
  chevronColor?: string
  chevronSize?: number
  topAlignIcon?: boolean
  positionChevronTop?: boolean
  showGradient?: boolean
  CustomExpansionIcon?: React.ReactElement
  heightWhenCollapsed?: number
  disableCollapsing?: boolean
  descriptionText?: string | React.ReactNode
  showDescriptionText?: boolean
  disableSubtitleExpansion?: boolean
}

const Container = styled(View)<{ backgroundColor: string; height?: number }>(({ theme, backgroundColor, height }) => {
  const paddingVertical = theme.spacing['16px']
  const [paddingVerticalNumber, _] = paddingVertical.split('px')
  return { paddingVertical, backgroundColor, height: height ? height + 2 * Number(paddingVerticalNumber) : 'auto' }
})

const Button = styled(IS_WEB ? Pressable : PressableOpacity)({
  borderRadius: 4,
})

const IconContainer = styled.View<{ topAlignIcon?: boolean }>(({ theme, topAlignIcon }) => ({
  marginRight: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : theme.spacing['16px'],
  ...(topAlignIcon && { alignSelf: 'flex-start' }),
}))

const TitleContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  position: 'relative',
})

const TitleTextContainer = styled.View(({ theme }) => ({
  flexShrink: 1,
  ...(theme.breakpoints.isMobileSized && { marginRight: theme.spacing['8px'] }),
}))

const Subtitle = styled(BodyText)(({ theme }) => ({
  marginTop: theme.spacing['8px'],
}))

const NotExpandableSubtitleContainer = styled.View(({ theme }) => ({
  marginLeft: theme.spacing['32px'],
}))

const ContentOuterContainer = styled(Animated.View)({
  position: 'relative',
  overflow: 'hidden',
  flexDirection: 'column',
})

const ContentInnerContainer = styled.View<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  position: 'absolute',
  width: '100%',
  paddingTop: theme.spacing['16px'],
  flexDirection: 'column',
  ...(IS_WEB && isOpen ? { visibility: 'visible' } : { visibility: 'hidden' }),
}))

const DescriptionContainer = styled(Animated.View)<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  overflow: 'hidden',
  position: 'absolute',
  bottom: theme.spacing['0px'],
  justifyContent: 'flex-end',
  marginLeft: theme.spacing['4px'],
  marginRight: theme.spacing['4px'],
  marginBottom: theme.spacing['4px'],
  ...(IS_WEB && isOpen ? { visibility: 'visible' } : { visibility: 'hidden' }),
}))

const ChevronContainer = styled(Animated.View)({
  marginLeft: 'auto',
})

const Gradient = styled(LinearGradient)`
  position: absolute;
  bottom: -16px;
  left: 0;
  right: 0;
  height: 16px;
`

/**
 * A component to show/hide additional content
 */
export const Accordion = forwardRef<AccordionHandle, AccordionProps>(
  (
    {
      title,
      subtitle,
      content,
      iconComponent,
      testIDPrefix = 'Accordion',
      titleColor,
      subtitleColor,
      subtitleSize,
      chevronColor,
      chevronSize,
      backgroundColor,
      handleIsExpandedChanged,
      hideExpansionIcon = false,
      onAccordionPress,
      onAccordianStateChanged,
      style,
      titleStyle,
      titleProps,
      topAlignIcon = false,
      buttonStyle,
      contentInnerContainerStyle,
      isExpanded,
      initiallyOpen = false,
      positionChevronTop,
      showGradient = true,
      CustomExpansionIcon,
      heightWhenCollapsed,
      disableCollapsing = false,
      showDescriptionText = false,
      descriptionText,
      disableSubtitleExpansion = false,
    },
    ref,
  ) => {
    // Durations / easings
    const RESIZE_ANIMATION_DURATION_IN_MS = 300

    const isWithinGroup = isBoolean(isExpanded) && handleIsExpandedChanged
    const titleId = `${testIDPrefix}-title`
    const subtitleId = `${testIDPrefix}-subtitle`
    const contentId = `${testIDPrefix}-content`

    const { colors, isAuroraTheme } = useTheme()
    const { formatMessage } = useIntl()
    const [isOpen, setIsOpen] = useState(initiallyOpen ?? isExpanded)

    // Content/Description measured heights (cached)
    const contentHeight = useRef<number>(0)
    const descriptionHeightRef = useRef<number>(0)

    // Shared animated heights (these drive the outer container height)
    const sharedContentHeight = useSharedValue(0)
    const sharedDescriptionHeight = useSharedValue(0)

    // Chevron rotation
    const sharedRotation = useSharedValue(isExpanded ? -180 : 0)

    // Description visual fade — ONLY the description uses this
    const descOpacity = useSharedValue(showDescriptionText ? 1 : 0)

    // Guards to avoid jank on first measure
    const didMeasureContent = useRef(false)
    const didMeasureDescription = useRef(false)

    // Container height = sum(content, description)
    const containerAnimatedStyle = useAnimatedStyle(() => {
      'worklet'
      return {
        height: sharedContentHeight.value + sharedDescriptionHeight.value,
      }
    })

    const animatedIconStyles = useAnimatedStyle(() => {
      'worklet'
      return {
        transform: [{ rotateZ: `${sharedRotation.value}deg` }],
        ...(positionChevronTop && { top: 0, right: 0, position: 'absolute' }),
      }
    })

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsOpen(true)
      },
      close: () => {
        setIsOpen(false)
      },
    }))

    // Sync controlled isExpanded with local state when in a group
    useEffect(() => {
      isWithinGroup && isExpanded !== isOpen && setIsOpen(isExpanded)
    }, [isOpen, isExpanded, isWithinGroup])

    // Open/close animation drives both sections’ heights and chevron rotation
    useEffect(() => {
      const animationTiming = {
        open: {
          duration: window.Cypress ? 0 : ACCORDION_OPEN_ANIMATION_DURATION_IN_MS,
          easing: Easing.bezier(0.53, 0.08, 0.04, 1),
        },
        close: {
          duration: window.Cypress ? 0 : ACCORDION_CLOSE_ANIMATION_DURATION_IN_MS,
          easing: Easing.bezier(0.9, -0.01, 0.68, 0.87),
        },
      }

      const newHeight = isOpen ? contentHeight.current : 0
      const newRotation = isOpen ? -180 : 0
      const targetDescriptionHeight = isOpen && showDescriptionText ? descriptionHeightRef.current : 0

      const timingOptions = isOpen ? animationTiming.open : animationTiming.close
      sharedContentHeight.value = withTiming(newHeight, timingOptions)
      sharedRotation.value = withTiming(newRotation, timingOptions)

      // For description during open, still respect fade-in for its height if showing
      sharedDescriptionHeight.value = withTiming(targetDescriptionHeight, timingOptions)
    }, [isOpen, showDescriptionText, sharedContentHeight, sharedDescriptionHeight, sharedRotation])

    // Description fade visual (opacity) — only when showing
    useEffect(() => {
      if (isOpen && showDescriptionText) {
        // Reset then fade-in
        descOpacity.value = 0
        descOpacity.value = withTiming(1, { duration: 500, easing: Easing.ease })
      } else {
        // Quick fade-out; height will be driven separately below
        descOpacity.value = withTiming(0, { duration: 120 })
      }
    }, [isOpen, showDescriptionText, descOpacity])

    const descAnimatedStyle = useAnimatedStyle(() => {
      'worklet'
      return {
        opacity: descOpacity.value,
      }
    })

    // Separate layout handlers
    const onContentLayout = (e: LayoutChangeEvent) => {
      const height = e.nativeEvent.layout.height
      const prev = contentHeight.current
      contentHeight.current = height

      // First measure: set initial height (skip animation)
      if (!didMeasureContent.current) {
        didMeasureContent.current = true
        sharedContentHeight.value = isOpen ? height : 0
        return
      }

      // Later: animate size changes only when open
      if (isOpen && Math.round(prev) !== Math.round(height)) {
        sharedContentHeight.value = withTiming(height, {
          duration: RESIZE_ANIMATION_DURATION_IN_MS,
          easing: Easing.ease,
        })
      }
    }

    const onDescriptionLayout = (e: LayoutChangeEvent) => {
      const height = e.nativeEvent.layout.height
      const prev = descriptionHeightRef.current
      descriptionHeightRef.current = height

      // First measure: set initial height (skip animation)
      if (!didMeasureDescription.current) {
        didMeasureDescription.current = true
        sharedDescriptionHeight.value = isOpen && showDescriptionText ? height : 0
        return
      }

      // When toggling showDescriptionText or description content changes,
      // animate its height independently. Use fade-in only when revealing.
      if (isOpen) {
        const target = showDescriptionText ? height : 0
        if (Math.round(prev) !== Math.round(target)) {
          sharedDescriptionHeight.value = withTiming(target, {
            duration: RESIZE_ANIMATION_DURATION_IN_MS,
            easing: showDescriptionText ? Easing.ease : Easing.ease,
          })
        }
      } else {
        // Closed: keep at 0 but keep cache updated
        sharedDescriptionHeight.value = 0
      }
    }

    const handleButtonPress = () => {
      onAccordionPress && !isOpen && onAccordionPress()
      if (isWithinGroup) {
        handleIsExpandedChanged(!isOpen)
      } else {
        onAccordianStateChanged && onAccordianStateChanged(!isOpen)
        setIsOpen(!isOpen)
      }
    }

    const getExpansionIcon = () => {
      if (disableCollapsing || hideExpansionIcon) return null

      return CustomExpansionIcon ? (
        React.cloneElement(CustomExpansionIcon, { isOpen })
      ) : (
        <ChevronContainer style={animatedIconStyles} testID={tID('ChevronIconContainer')}>
          <ChevronIcon
            fillColor={chevronColor || titleColor || colors.textPrimary}
            size={chevronSize}
            direction={ChevronIconDirection.DOWN}
          />
        </ChevronContainer>
      )
    }

    return (
      <Container
        backgroundColor={backgroundColor || getAuroraAwareBackgroundColor(isAuroraTheme, colors.backgroundPrimary)}
        height={heightWhenCollapsed}
        style={style}
      >
        <Button
          onPress={() => handleButtonPress()}
          testID={tID(titleId)}
          accessibilityRole='button'
          accessibilityLabel={formatMessage({
            defaultMessage: 'Expand content',
            description: 'button text expand content',
          })}
          accessibilityExpanded={isOpen}
          accessibilityControls={contentId}
          style={buttonStyle}
          disabled={disableCollapsing}
        >
          <TitleContainer>
            {Boolean(iconComponent) && <IconContainer topAlignIcon={topAlignIcon}>{iconComponent}</IconContainer>}
            <TitleTextContainer>
              <Subhead
                selectable={false}
                text={title}
                size={SubheadSize.SMALL}
                color={titleColor || colors.textPrimary}
                nativeID={titleId}
                wrap
                noRole
                style={{ ...(positionChevronTop && { marginRight: 32 }), ...titleStyle }}
                {...titleProps}
              />
              {subtitle && !disableSubtitleExpansion && (
                <Subtitle
                  selectable={false}
                  text={subtitle}
                  size={subtitleSize || BodyTextSize.DEFAULT}
                  color={subtitleColor || colors.textPrimary}
                  nativeID={subtitleId}
                  testID={tID(subtitleId)}
                />
              )}
            </TitleTextContainer>
            {getExpansionIcon()}
            {showGradient && !isAuroraTheme && (
              <Gradient
                colors={[
                  backgroundColor || colors.backgroundPrimary,
                  `${backgroundColor || colors.backgroundPrimary}00`,
                ]}
              />
            )}
          </TitleContainer>
        </Button>
        {subtitle && disableSubtitleExpansion && (
          <NotExpandableSubtitleContainer>
            <Subtitle
              selectable={false}
              text={subtitle}
              size={subtitleSize || BodyTextSize.DEFAULT}
              color={subtitleColor || colors.textPrimary}
              nativeID={subtitleId}
              testID={tID(subtitleId)}
            />
          </NotExpandableSubtitleContainer>
        )}
        {/* Height of this outer container is driven by the sum of both inner animated heights */}
        <ContentOuterContainer style={containerAnimatedStyle} testID={tID(contentId)}>
          {/* Main content: always mounted; measured separately */}
          <ContentInnerContainer
            isOpen={isOpen}
            onLayout={onContentLayout}
            accessibilityElementsHidden={!isOpen}
            accessibilityHidden={!isOpen}
            accessibilityLabelledBy={titleId}
            nativeID={contentId}
            style={contentInnerContainerStyle}
          >
            {content}
          </ContentInnerContainer>
          {/* Description: always mounted; height + fade/slide animate; ease-in only when showing */}
          {showDescriptionText && (
            <DescriptionContainer
              onLayout={onDescriptionLayout}
              style={descAnimatedStyle}
              collapsable={false}
              pointerEvents={showDescriptionText ? 'auto' : 'none'}
              accessibilityElementsHidden={!showDescriptionText}
              accessibilityHidden={!showDescriptionText}
              isOpen={isOpen}
              testID={tID(`${testIDPrefix}-descriptionText`)}
            >
              {typeof descriptionText === 'string' ? (
                <BodyText text={descriptionText} size={BodyTextSize.DEFAULT} color={colors.textSecondary} />
              ) : (
                descriptionText
              )}
            </DescriptionContainer>
          )}
        </ContentOuterContainer>
      </Container>
    )
  },
)
